import { useEffect, useRef, useCallback, useReducer, useState } from 'react';
import { useSocket, useSocketEvent } from 'socket.io-react-hook';
import { _getToken, positionOpenerAxiosApi } from 'src/Config/axiosApi';
import {
  OpenerGroupPodState,
  useOpenerGroupsPodState
} from '../api/useOpenerGroupPods';
import {
  GroupSelection,
  groupSelectionKey
} from '../components/entry/groupFilter';
import {
  EnrichedEntryStatus,
  EntryOrdersResponse,
  GroupCostSummary,
  OpeningPageState,
  TopOfBook,
  VenuePositionsMeta,
  VenuePositionsMetaFrame
} from '../types/entry.types';
import {
  createLiveStore,
  LiveStore
} from '../live/createLiveStore';
import {
  LatencySample,
  recordReceived,
  useLatencyTracker
} from './useLatencyTracker';

export type { EnrichedEntryStatus } from '../types/entry.types';

export interface EntryExecutionEvent {
  ruleId: string;
  pairName: string;
  makerPlatform: string;
  makerPrice: number;
  counterPrice: number;
  contracts: number;
  tnaPercent: number;
  volumeUsd: number;
}

export interface EntryErrorEvent {
  ruleId: string;
  pairName: string;
  state: string;
  errorMessage: string;
}

type Action =
  | { type: 'STATUS'; payload: EnrichedEntryStatus[] }
  | { type: 'STATUS_UPDATE'; payload: EnrichedEntryStatus[] }
  | { type: 'EXECUTION'; payload: EntryExecutionEvent }
  | { type: 'ERROR'; payload: EntryErrorEvent }
  | { type: 'ORDERS'; payload: EntryOrdersResponse };

interface State {
  /** Map of pairId AND ruleId -> live entry state (mirrors unwinding hook) */
  states: Map<string, EnrichedEntryStatus>;
  lastExecution: EntryExecutionEvent | null;
  lastError: EntryErrorEvent | null;
  orders: EntryOrdersResponse | null;
}

const initialState: State = {
  states: new Map(),
  lastExecution: null,
  lastError: null,
  orders: null
};

/**
 * Strip the server's `_meta` envelope before passing into the reducer.
 * Mirrors `useUnwindingSocket`. Server wraps non-array payloads as
 * `{ ...payload, _meta }` and array payloads as `{ data: payload, _meta }`.
 */
/**
 * Phase 6 — project an `OpeningPageState` back into the legacy
 * `EnrichedEntryStatus` shape so existing row components keep working
 * during the bridging release. When `rule === null`, fills rule-scoped
 * fields with placeholders so the row still renders the market data.
 * Phase 7 removes this projection once the row components consume
 * `OpeningPageState` directly.
 */
function projectToLegacy(p: OpeningPageState): EnrichedEntryStatus {
  const m = p.market;
  const r = p.rule;
  const displayPath: 'a' | 'b' | null =
    r?.committedPath ??
    (r?.sumPathA != null && r?.sumPathB != null
      ? r.sumPathA <= r.sumPathB
        ? 'a'
        : 'b'
      : null);
  const polyBid =
    displayPath === 'b' ? m.polyNoBid : m.polyYesBid;
  const polyAsk =
    displayPath === 'b' ? m.polyNoAsk : m.polyYesAsk;
  const kalshiBid =
    displayPath === 'b' ? m.kalshiYesBid : m.kalshiNoBid;
  const kalshiAsk =
    displayPath === 'b' ? m.kalshiYesAsk : m.kalshiNoAsk;
  return {
    ruleId: r?.ruleId ?? p.pairId,
    pairId: p.pairId,
    state: r?.state ?? 'no_rule',
    makerPlatform: r?.makerPlatform ?? 'kalshi',
    makerOrderId: r?.makerOrderId ?? null,
    makerPrice: r?.makerPrice ?? null,
    makerFilledQty: r?.makerFilledQty ?? 0,
    makerFilledPrice: r?.makerFilledPrice ?? 0,
    counterOrderId: r?.counterOrderId ?? null,
    counterFilledQty: r?.counterFilledQty ?? 0,
    lastStateChange: r?.lastStateChange ?? new Date().toISOString(),
    errorMessage: r?.errorMessage ?? null,
    pairName: m.pairName,
    expiryDate: m.expiryDate,
    daysToExpiry: m.daysToExpiry,
    polyBid,
    polyAsk,
    kalshiBid,
    kalshiAsk,
    currentSumOfAsks:
      r?.committedPath === 'b'
        ? r?.sumPathB ?? null
        : r?.committedPath === 'a'
          ? r?.sumPathA ?? null
          : (r?.sumPathA ?? r?.sumPathB ?? null),
    simulation: r?.simulation ?? {
      polyAsMaker: null,
      kalshiAsMaker: null
    },
    kalshiConfig: r?.kalshiConfig ?? null,
    polymarketConfig: r?.polymarketConfig ?? null,
    maxVolumeContracts: r?.maxVolumeContracts ?? 0,
    maxVolumeHit: r?.maxVolumeHit ?? null,
    volumePeriod: r?.volumePeriod ?? 'day',
    volumeUsed: r?.volumeUsed ?? 0,
    volumeRemaining: r?.volumeRemaining ?? 0,
    kalshiMarketUrl: m.kalshiMarketUrl,
    polymarketMarketUrl: m.polymarketMarketUrl,
    simTna: r?.simTna,
    simSize: r?.simSize,
    simMaxVolume: r?.simMaxVolume,
    appliedSimTna: r?.appliedSimTna,
    appliedSimSize: r?.appliedSimSize,
    appliedSimMaxVolume: r?.appliedSimMaxVolume,
    hasPendingSim: r?.hasPendingSim,
    polyYesBid: m.polyYesBid,
    polyYesAsk: m.polyYesAsk,
    polyNoBid: m.polyNoBid,
    polyNoAsk: m.polyNoAsk,
    kalshiYesBid: m.kalshiYesBid,
    kalshiYesAsk: m.kalshiYesAsk,
    kalshiNoBid: m.kalshiNoBid,
    kalshiNoAsk: m.kalshiNoAsk,
    // Sums + sim cells are computed at the envelope level (always,
    // rule or no rule) — read from `p.*` first so pre-rule rows
    // still see live values. Fall back to the rule slice for
    // backward-compat with older backend builds that emitted them
    // there.
    sumPathA: p.sumPathA ?? r?.sumPathA ?? null,
    sumPathB: p.sumPathB ?? r?.sumPathB ?? null,
    lowerPath: p.lowerPath ?? r?.lowerPath ?? null,
    committedPath: r?.committedPath ?? null,
    simPolyYesMaker: p.simPolyYesMaker ?? r?.simPolyYesMaker ?? null,
    simPolyNoMaker: p.simPolyNoMaker ?? r?.simPolyNoMaker ?? null,
    simKalshiYesMaker: p.simKalshiYesMaker ?? r?.simKalshiYesMaker ?? null,
    simKalshiNoMaker: p.simKalshiNoMaker ?? r?.simKalshiNoMaker ?? null,
    engineSim: p.engineSim ?? null,
    believedPosition: p.believedPosition ?? null,
    ruleCost: p.ruleCost ?? null,
    venuePositions: p.venuePositions ?? null,
    // Per-pair opener kill switch. Defaults false on manual-confirm, so
    // the row should render OFF when the field is unset. Forwarding it
    // verbatim here lets the row's switch read live state without
    // hitting REST.
    pairEnabled: p.pairEnabled === true,
    // The rule's REAL armed state — the AUTHORITATIVE source for the per-row
    // on/off switch. Forwarded VERBATIM (not coerced to false): `undefined`
    // must survive so the switch's back-compat fallback to `pairEnabled` can
    // kick in on envelopes from an older backend that doesn't emit it yet.
    ruleArmed: p.ruleArmed
  };
}

/**
 * The server's emit time off the `_meta` envelope. Freshness is aged against
 * this rather than the browser clock, so a skewed client cannot read a dead
 * venue poll as fresh. `null` on a payload without the envelope.
 */
export function readServerEmittedAt(message: unknown): number | null {
  const meta = (message as { _meta?: { serverEmittedAt?: unknown } } | null)
    ?._meta;
  const at = meta?.serverEmittedAt;
  return typeof at === 'number' && Number.isFinite(at) ? at : null;
}

function unwrap<T>(message: unknown): T {
  if (Array.isArray(message)) return message as T;
  if (
    message &&
    typeof message === 'object' &&
    '_meta' in (message as object)
  ) {
    const obj = message as Record<string, unknown> & { _meta?: unknown };
    if ('data' in obj) return obj.data as T;
    const rest = { ...obj };
    delete rest._meta;
    return rest as T;
  }
  return message as T;
}

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'STATUS': {
      const newMap = new Map<string, EnrichedEntryStatus>();
      for (const s of action.payload) {
        newMap.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) newMap.set(s.pairId, s);
      }
      return { ...state, states: newMap };
    }
    case 'STATUS_UPDATE': {
      const merged = new Map(state.states);
      for (const s of action.payload) {
        merged.set(s.ruleId, s);
        if (s.pairId && s.pairId !== s.ruleId) merged.set(s.pairId, s);
      }
      return { ...state, states: merged };
    }
    case 'EXECUTION':
      return { ...state, lastExecution: action.payload };
    case 'ERROR':
      return { ...state, lastError: action.payload };
    case 'ORDERS':
      return { ...state, orders: action.payload };
    default:
      return state;
  }
}

export { initialState as entrySocketInitialState };

/* ------------------------------------------------------------------ */
/* Live store (per-pair external store — render isolation)            */
/* ------------------------------------------------------------------ */

/**
 * Compare two `TopOfBook | null` by VALUE. The projection re-allocates
 * these objects on every WS frame even when the quote is unchanged, so a
 * reference compare would never skip — we compare price + size instead.
 */
const topEqual = (
  a: TopOfBook | null | undefined,
  b: TopOfBook | null | undefined
): boolean => {
  if (a == null || b == null) return a == b;
  return a.price === b.price && a.size === b.size;
};

/** Cheap structural compare for the nested sim / position objects. */
const deepEqual = (a: unknown, b: unknown): boolean => {
  if (a === b) return true;
  if (a == null || b == null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return false;
  const ak = Object.keys(a as object);
  const bk = Object.keys(b as object);
  if (ak.length !== bk.length) return false;
  for (const k of ak) {
    if (
      !deepEqual(
        (a as Record<string, unknown>)[k],
        (b as Record<string, unknown>)[k]
      )
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Render-equality for the ACT cell's `ruleCost`. The backend re-emits every
 * rule's `RuleCost` on a cadence with a fresh `computedAt`, so comparing the
 * WHOLE object (incl. `computedAt` and the `spend/fees/matched/livePnl` fields
 * the ACT cell never renders) marks every rule-bearing row "changed" every ~2s
 * even when the displayed value is identical. Compare ONLY the two fields the
 * UI renders — `kalshiNet` / `polyNet` — treating absent objects as equal.
 * `ActNetCell` reads `computedAt` off the latest stored value at render time,
 * so excluding it here does not break the staleness check.
 */
const ruleCostRenderEqual = (
  a: EnrichedEntryStatus['ruleCost'],
  b: EnrichedEntryStatus['ruleCost']
): boolean => {
  if (!a || !b) return !a && !b;
  return a.kalshiNet === b.kalshiNet && a.polyNet === b.polyNet;
};

/**
 * Structural equality over the RENDERED fields of `EnrichedEntryStatus`
 * (the row cells, the sort metrics, and the engine-position drawer). When
 * `next` is render-equivalent to `prev` the store skips the update — no
 * version bump, no per-row notify. Fields the UI never reads (order ids,
 * filled qty, lastStateChange, etc.) are deliberately excluded so they
 * don't force a re-render on their own.
 */
const entryStateEqual = (
  prev: EnrichedEntryStatus,
  next: EnrichedEntryStatus
): boolean =>
  prev.pairEnabled === next.pairEnabled &&
  prev.ruleArmed === next.ruleArmed &&
  prev.state === next.state &&
  prev.pairName === next.pairName &&
  prev.expiryDate === next.expiryDate &&
  prev.daysToExpiry === next.daysToExpiry &&
  prev.makerPlatform === next.makerPlatform &&
  prev.makerPrice === next.makerPrice &&
  prev.errorMessage === next.errorMessage &&
  prev.currentSumOfAsks === next.currentSumOfAsks &&
  prev.sumPathA === next.sumPathA &&
  prev.sumPathB === next.sumPathB &&
  prev.lowerPath === next.lowerPath &&
  prev.committedPath === next.committedPath &&
  prev.kalshiMarketUrl === next.kalshiMarketUrl &&
  prev.polymarketMarketUrl === next.polymarketMarketUrl &&
  prev.maxVolumeContracts === next.maxVolumeContracts &&
  prev.volumePeriod === next.volumePeriod &&
  prev.volumeUsed === next.volumeUsed &&
  prev.volumeRemaining === next.volumeRemaining &&
  topEqual(prev.polyBid, next.polyBid) &&
  topEqual(prev.polyAsk, next.polyAsk) &&
  topEqual(prev.kalshiBid, next.kalshiBid) &&
  topEqual(prev.kalshiAsk, next.kalshiAsk) &&
  topEqual(prev.polyYesBid, next.polyYesBid) &&
  topEqual(prev.polyYesAsk, next.polyYesAsk) &&
  topEqual(prev.polyNoBid, next.polyNoBid) &&
  topEqual(prev.polyNoAsk, next.polyNoAsk) &&
  topEqual(prev.kalshiYesBid, next.kalshiYesBid) &&
  topEqual(prev.kalshiYesAsk, next.kalshiYesAsk) &&
  topEqual(prev.kalshiNoBid, next.kalshiNoBid) &&
  topEqual(prev.kalshiNoAsk, next.kalshiNoAsk) &&
  deepEqual(prev.engineSim, next.engineSim) &&
  deepEqual(prev.simulation, next.simulation) &&
  deepEqual(prev.believedPosition, next.believedPosition) &&
  ruleCostRenderEqual(prev.ruleCost, next.ruleCost) &&
  // Safe to deep-compare WHOLE: unlike `ruleCost`, the venue-positions payload
  // carries no `computedAt`/timestamp, so a re-poll that finds the same
  // contracts is byte-identical and never forces a re-render.
  deepEqual(prev.venuePositions, next.venuePositions) &&
  deepEqual(prev.maxVolumeHit, next.maxVolumeHit) &&
  deepEqual(prev.simPolyYesMaker, next.simPolyYesMaker) &&
  deepEqual(prev.simPolyNoMaker, next.simPolyNoMaker) &&
  deepEqual(prev.simKalshiYesMaker, next.simKalshiYesMaker) &&
  deepEqual(prev.simKalshiNoMaker, next.simKalshiNoMaker);

/**
 * Preserve the fields that ride the split-shape `opening_state*` stream ONLY
 * so a frame/seed that OMITS them never clobbers the value an earlier
 * `opening_state*` frame set. Mirrors the unwinder's `mergePreservedFields`.
 *
 * The hazard is `ruleArmed`: the `opening_state` WS envelope carries it, but
 * the `/states` REST seed (whose backend `EnrichedRuleState` payload has NO
 * `ruleArmed`) and any legacy `entry_status` frame OMIT it. Every path writes
 * this same store, so a seed arriving with `ruleArmed: undefined` — e.g. the
 * REST reseed that fires on every socket reconnect — would wipe the
 * authoritative armed bit. `resolveWsArmed` then falls back to `pairEnabled`,
 * so a PAUSED-but-pair-enabled rule (`ruleArmed:false, pairEnabled:true`)
 * would flip its per-row switch ON while the rule is actually paused — the
 * exact lie the durable-armed state exists to prevent. Preserve the last
 * known value whenever an incoming entry doesn't carry the field.
 *
 * `pairEnabled` / `engineSim` / `believedPosition` / `ruleCost` /
 * `venuePositions` share the same dual-path hazard (present on
 * `opening_state*`, absent from the REST seed), so they are preserved the same
 * way. For `venuePositions` the consequence is visible: the reseed fires on
 * EVERY socket reconnect, so without this the ACT / YES / NO cells would blank
 * out on each reconnect.
 */
function mergePreservedFields(
  prev: EnrichedEntryStatus | undefined,
  incoming: EnrichedEntryStatus
): EnrichedEntryStatus {
  if (!prev) return incoming;
  let out = incoming;
  if (incoming.ruleArmed === undefined && prev.ruleArmed !== undefined) {
    out = { ...out, ruleArmed: prev.ruleArmed };
  }
  if (incoming.pairEnabled === undefined && prev.pairEnabled !== undefined) {
    out = { ...out, pairEnabled: prev.pairEnabled };
  }
  if (incoming.engineSim === undefined && prev.engineSim !== undefined) {
    out = { ...out, engineSim: prev.engineSim };
  }
  if (
    incoming.believedPosition === undefined &&
    prev.believedPosition !== undefined
  ) {
    out = { ...out, believedPosition: prev.believedPosition };
  }
  if (incoming.ruleCost === undefined && prev.ruleCost !== undefined) {
    out = { ...out, ruleCost: prev.ruleCost };
  }
  if (
    incoming.venuePositions === undefined &&
    prev.venuePositions !== undefined
  ) {
    out = { ...out, venuePositions: prev.venuePositions };
  }
  return out;
}

/**
 * Factory for the opener live store. Dual-keyed by `ruleId` AND `pairId`
 * (deduped) mirroring the legacy `pairId ?? ruleId` lookup. `merge` ports the
 * unwinder's field-preservation so a frame/seed omitting `ruleArmed` (the
 * `/states` REST reseed on reconnect / a legacy frame) never clobbers the
 * authoritative armed bit the per-row switch reads.
 */
export function createEntryLiveStore(): LiveStore<EnrichedEntryStatus> {
  return createLiveStore<EnrichedEntryStatus>({
    keysOf: s => {
      const keys: string[] = [];
      if (s.ruleId) keys.push(s.ruleId);
      if (s.pairId && s.pairId !== s.ruleId) keys.push(s.pairId);
      return keys;
    },
    equals: entryStateEqual,
    merge: mergePreservedFields
  });
}

/* ------------------------------------------------------------------ */
/* Socket target resolution (group feed pods vs core gateway)          */
/* ------------------------------------------------------------------ */

export interface EntrySocketTarget {
  url: string;
  path?: string;
}

interface EntrySocketEnv {
  VITE_OPENER_FEED_URL?: string;
  VITE_POSITION_OPENER_URL?: string;
}

/** Today's default target — the always-on core gateway. */
export function coreEntrySocketTarget(
  env: EntrySocketEnv = import.meta.env
): EntrySocketTarget {
  return { url: env.VITE_POSITION_OPENER_URL || '' };
}

/**
 * Pick the Socket.io target for the current group selection. A specific
 * group whose feed pod is Ready connects to the shared opener-feed ALB on
 * the group's path; everything else ('all', 'ungrouped', group not ready,
 * group unknown, no pod state, env var unset) uses the core gateway —
 * the 'all'/'ungrouped' views NEVER depend on feed pods.
 */
export function resolveEntrySocketTarget(
  selection: GroupSelection | null | undefined,
  podState: OpenerGroupPodState[] | null | undefined,
  env: EntrySocketEnv = import.meta.env
): EntrySocketTarget {
  const core = coreEntrySocketTarget(env);
  if (!selection || selection.kind !== 'group') return core;
  if (!env.VITE_OPENER_FEED_URL) return core;
  const group = podState?.find(g => g.groupId === selection.groupId);
  if (!group || group.feedReady !== true) return core;
  return {
    url: env.VITE_OPENER_FEED_URL,
    path: `/groups/${selection.groupId}/socket.io`
  };
}

/**
 * Layer the connect_error fallback on top of the resolution: once a feed
 * target has failed to connect, stick to the core gateway until the
 * selection changes (the hook resets the flag on selection change).
 */
export function effectiveEntrySocketTarget(
  selection: GroupSelection | null | undefined,
  podState: OpenerGroupPodState[] | null | undefined,
  feedFallback: boolean,
  env: EntrySocketEnv = import.meta.env
): EntrySocketTarget {
  const resolved = resolveEntrySocketTarget(selection, podState, env);
  return feedFallback && resolved.path ? coreEntrySocketTarget(env) : resolved;
}

export function useEntrySocket(groupSelection?: GroupSelection) {
  const accessToken = _getToken();

  // Pod state only matters while a specific group is selected — the poll
  // is gated off for the 'all'/'ungrouped' views (always core target).
  const { data: podState } = useOpenerGroupsPodState({
    enabled: groupSelection?.kind === 'group'
  });

  // Sticky core fallback after a feed connect_error; reset when the
  // operator switches the group selection.
  const [feedFallback, setFeedFallback] = useState(false);
  const selectionKey = groupSelection
    ? groupSelectionKey(groupSelection)
    : 'all';
  useEffect(() => {
    setFeedFallback(false);
  }, [selectionKey]);

  const target = effectiveEntrySocketTarget(
    groupSelection,
    podState,
    feedFallback
  );
  const wsUrl = target.url;

  const { socket } = useSocket((wsUrl || '') + '/opening', {
    enabled: !!wsUrl && !!accessToken,
    transports: ['websocket'],
    ...(target.path ? { path: target.path } : {}),
    auth: (cb: (data: { token: string }) => void) =>
      cb({ token: _getToken() })
  });

  // While connected through a feed pod, a connect_error (pod gone, ALB
  // path dead, auth handshake refused) degrades to the core gateway.
  useEffect(() => {
    if (!target.path) return;
    const onConnectError = () => setFeedFallback(true);
    socket.on('connect_error', onConnectError);
    return () => {
      socket.off('connect_error', onConnectError);
    };
  }, [socket, target.path]);

  const [state, dispatch] = useReducer(reducer, initialState);

  // The engine's per-GROUP live trading summary (descalce + contracts + money),
  // pushed on the `/opening` socket as `group_cost_update`. The payload now
  // carries its own `groupId` (the feed pod stamps it), and the strip rejects a
  // summary whose `groupId` doesn't match the selected group's `_id` — so a
  // stale or cross-group aggregate can't bleed under a new group's header.
  // Still cleared whenever the socket target changes (group switch / core↔feed
  // fallback) so the previous group's summary doesn't linger during handoff.
  const [groupCost, setGroupCost] = useState<GroupCostSummary | null>(null);

  // Provenance + freshness for the per-pair `venuePositions`, pushed by the
  // same pod that serves the pairs, kept WITH its receive time and the server's
  // emit time so freshness can be aged in durations instead of across clocks.
  // `null` = no meta seen on this socket (the CORE gateway case).
  const [venueFrame, setVenueFrame] =
    useState<VenuePositionsMetaFrame | null>(null);

  // Tracks WHICH socket instance we subscribed on — switching targets
  // (core ↔ feed, group ↔ group) hands us a new socket that needs its own
  // subscribe_entry even though the previous one already subscribed.
  const subscribedSocket = useRef<unknown>(null);

  // Per-pair external store for the high-frequency live price/sim state.
  // WS frames mutate it in plain JS (no setState) so only rows whose pair
  // actually changed re-render. Created once per hook instance.
  const storeRef = useRef<LiveStore<EnrichedEntryStatus> | null>(null);
  if (storeRef.current === null) storeRef.current = createEntryLiveStore();
  const entryLiveStore = storeRef.current;

  // REST seed: same pattern as `useUnwindingSocket` — fetch the latest
  // EnrichedEntryStatus[] snapshot when the WS isn't connected so the page
  // has data during the handshake or while the predictions subsystem is
  // OFF. Live WS deltas always override.
  useEffect(() => {
    if (!wsUrl || !accessToken) return;
    if (socket.connected) return;

    let cancelled = false;
    positionOpenerAxiosApi
      .get<EnrichedEntryStatus[]>('/states')
      .then(({ data }) => {
        if (cancelled) return;
        if (!Array.isArray(data)) return;
        entryLiveStore.applyUpdate(data);
      })
      .catch(err => {
        if (cancelled) return;
        console.warn('[EntrySocket] REST seed failed:', err?.message ?? err);
      });

    return () => {
      cancelled = true;
    };
  }, [wsUrl, accessToken, socket.connected, entryLiveStore]);

  const latencyBuf = useRef<LatencySample[]>([]);
  useLatencyTracker(socket, latencyBuf);

  // Subscribe on every connect/reconnect.
  useEffect(() => {
    const subscribeIfNeeded = () => {
      if (subscribedSocket.current !== socket) {
        socket.emit('subscribe_entry');
        subscribedSocket.current = socket;
      }
    };
    const markDisconnected = () => {
      subscribedSocket.current = null;
    };

    socket.on('connect', subscribeIfNeeded);
    socket.on('disconnect', markDisconnected);

    if (socket.connected) subscribeIfNeeded();
    else markDisconnected();

    return () => {
      socket.off('connect', subscribeIfNeeded);
      socket.off('disconnect', markDisconnected);
    };
  }, [socket, socket.connected]);

  // This hook deliberately does NOT listen to the legacy `entry_status` /
  // `entry_status_update` events. The backend emits them alongside
  // `opening_state*` for old FE builds during a deploy, but they carry
  // the legacy `EnrichedRuleState` shape WITHOUT `engineSim`. Consuming
  // both streams into one Map (last-write-wins) made every
  // `entry_status_update` clobber the `engineSim` that the preceding
  // `opening_state_update` had set — the Sim columns flickered
  // value ↔ "—" on every tick. `opening_state*` is a superset (every
  // manual-confirmed pair, rule or not), so it is the single source of
  // truth here. Do not re-add the legacy listeners.
  const { lastMessage: executionMessage } =
    useSocketEvent<EntryExecutionEvent>(socket, 'entry_execution');

  const { lastMessage: errorMessage } = useSocketEvent<EntryErrorEvent>(
    socket,
    'entry_error'
  );

  useEffect(() => {
    if (executionMessage) {
      recordReceived(latencyBuf.current, 'entry_execution', executionMessage);
      dispatch({
        type: 'EXECUTION',
        payload: unwrap<EntryExecutionEvent>(executionMessage)
      });
    }
  }, [executionMessage]);

  useEffect(() => {
    if (errorMessage) {
      recordReceived(latencyBuf.current, 'entry_error', errorMessage);
      dispatch({
        type: 'ERROR',
        payload: unwrap<EntryErrorEvent>(errorMessage)
      });
    }
  }, [errorMessage]);

  // ── Phase 6 — split-shape listeners. Backend emits these alongside
  //    the legacy `entry_status` / `entry_status_update` for one
  //    bridging release. We project envelopes back to the legacy shape
  //    so the existing reducer + row components keep working. Phase 7
  //    deletes the legacy listeners + projection.
  const { lastMessage: openingStateMessage } = useSocketEvent<
    OpeningPageState[]
  >(socket, 'opening_state');
  const { lastMessage: openingStateUpdateMessage } = useSocketEvent<
    OpeningPageState[]
  >(socket, 'opening_state_update');

  useEffect(() => {
    if (openingStateMessage) {
      recordReceived(
        latencyBuf.current,
        'opening_state',
        openingStateMessage
      );
      const payload = unwrap<OpeningPageState[]>(openingStateMessage);
      if (!Array.isArray(payload)) return;
      // Full snapshot → applySnapshot (evicts pairs no longer present),
      // mirroring the old reducer's Map-replace prune.
      entryLiveStore.applySnapshot(payload.map(projectToLegacy));
    }
  }, [openingStateMessage, entryLiveStore]);

  useEffect(() => {
    if (openingStateUpdateMessage) {
      recordReceived(
        latencyBuf.current,
        'opening_state_update',
        openingStateUpdateMessage
      );
      const payload = unwrap<OpeningPageState[]>(openingStateUpdateMessage);
      if (!Array.isArray(payload)) return;
      // Incremental delta → applyUpdate (changed keys only, no eviction).
      entryLiveStore.applyUpdate(payload.map(projectToLegacy));
    }
  }, [openingStateUpdateMessage, entryLiveStore]);

  const { lastMessage: ordersMessage } = useSocketEvent<EntryOrdersResponse>(
    socket,
    'entry_orders'
  );

  useEffect(() => {
    if (ordersMessage) {
      recordReceived(latencyBuf.current, 'entry_orders', ordersMessage);
      dispatch({
        type: 'ORDERS',
        payload: unwrap<EntryOrdersResponse>(ordersMessage)
      });
    }
  }, [ordersMessage]);

  // Per-GROUP live summary (WP-F). The gateway seeds it on subscribe and
  // re-emits on its broadcast tick (coalesced by `computedAt`).
  const { lastMessage: groupCostMessage } = useSocketEvent<GroupCostSummary>(
    socket,
    'group_cost_update'
  );

  const { lastMessage: venueMetaMessage } = useSocketEvent<VenuePositionsMeta>(
    socket,
    'venue_positions_meta'
  );

  // Clear the per-GROUP cost summary the moment the socket target changes — a
  // new group (or the core↔feed fallback) hands us a fresh socket, and
  // `lastMessage` retains the prior socket's payload until the new pod emits.
  useEffect(() => {
    setGroupCost(null);
  }, [socket]);

  // The venue meta is reset only when the operator switches to a DIFFERENT
  // group — NOT on a same-group socket swap (a reconnect after a ping timeout,
  // or the core↔feed fallback). Cross-group safety is already enforced at render
  // by the `groupId` guard below (`activeVenueFrame`), so clearing on the raw
  // socket identity was redundant AND was what blanked the ACT / YES / NO cells
  // on every reconnect: a `testAccount` group (whose main-account REST may NOT
  // stand in) flickered value ↔ "—" each time its busy feed pod dropped and
  // re-established the socket. Holding the last frame lets a brief reconnect keep
  // rendering the still-fresh counts; a gap longer than `staleMs` still ages the
  // read to stale on its own.
  const venueGroupId =
    groupSelection?.kind === 'group' ? groupSelection.groupId : null;
  useEffect(() => {
    setVenueFrame(null);
  }, [venueGroupId]);

  useEffect(() => {
    if (venueMetaMessage) {
      recordReceived(
        latencyBuf.current,
        'venue_positions_meta',
        venueMetaMessage
      );
      setVenueFrame({
        meta: unwrap<VenuePositionsMeta>(venueMetaMessage),
        receivedAt: Date.now(),
        receivedMono: performance.now(),
        serverEmittedAt: readServerEmittedAt(venueMetaMessage)
      });
    }
  }, [venueMetaMessage]);

  useEffect(() => {
    if (groupCostMessage) {
      recordReceived(latencyBuf.current, 'group_cost_update', groupCostMessage);
      setGroupCost(unwrap<GroupCostSummary>(groupCostMessage));
    }
  }, [groupCostMessage]);

  // Stable `getState` — a non-reactive point read off the live store. The
  // high-frequency price/sim stream now lives in `entryLiveStore` (rows
  // subscribe per-key via `useLiveState`), so this no longer flips
  // identity on a WS tick. Kept for non-row callers that want a one-shot
  // snapshot (e.g. the "Engine position" drawer reading `believedPosition`
  // for the selected rule). Row components must NOT use this — they read
  // reactively from the store so only the changed row re-renders.
  const getState = useCallback(
    (idOrPair: string): EnrichedEntryStatus | undefined =>
      entryLiveStore.getKeySnapshot(idOrPair),
    [entryLiveStore]
  );

  const injectStates = useCallback(
    (states: EnrichedEntryStatus[]) => {
      if (!Array.isArray(states)) {
        console.warn(
          '[EntrySocket] injectStates called with non-array, ignoring:',
          states
        );
        return;
      }
      entryLiveStore.applyUpdate(states);
    },
    [entryLiveStore]
  );

  // The meta is only ever this GROUP's meta. Clearing it in an effect on the
  // socket change leaves one render where the previous group's account label
  // and staleness horizon sit above the newly selected group's rows (the
  // socket ref updates in an effect too), so the match is made HERE, during
  // render: a frame whose `groupId` is not the selected group is not shown at
  // all. Same rejection the group-cost strip does with its own `groupId`.
  const activeVenueFrame =
    venueFrame &&
    groupSelection?.kind === 'group' &&
    venueFrame.meta.groupId === groupSelection.groupId
      ? venueFrame
      : null;

  return {
    isConnected: socket.connected,
    store: entryLiveStore,
    getState,
    injectStates,
    groupCost,
    venueFrame: activeVenueFrame,
    lastExecution: state.lastExecution,
    lastError: state.lastError,
    orders: state.orders
  };
}
